const getDefaultConfig = () => {
    const configElement = document.getElementById('brandguide-search-config');

    if (!configElement?.textContent) {
        return {};
    }

    try {
        return JSON.parse(configElement.textContent);
    } catch (error) {
        console.error(error);
        return {};
    }
};

export default (config = null) => {
    const resolvedConfig = config ?? getDefaultConfig();

    return ({
    sidebarOpen: false,
    searchOpen: false,
    searchQuery: '',
    searchLoading: false,
    searchResults: [],
    searchTotalCount: 0,
    searchAbortController: null,
    searchDebounceTimer: null,
    searchConfig: {
        rootId: resolvedConfig.rootId ?? 0,
        searchUrl: resolvedConfig.searchUrl ?? '',
        texts: resolvedConfig.texts ?? {}
    },

    init() {
        this.searchQuery = '';
    },

    openSearch() {
        this.searchOpen = true;

        this.$nextTick(() => {
            this.$refs.searchInput?.focus();
            this.$refs.searchInput?.select?.();
        });
    },

    closeSearch() {
        this.searchOpen = false;
        this.cancelPendingSearch();
    },

    onSearchInput() {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = window.setTimeout(() => {
            this.performSearch();
        }, 275);
    },

    async performSearch() {
        const query = (this.searchQuery ?? '').trim();

        if (query.length < 2) {
            this.cancelPendingSearch();
            this.searchLoading = false;
            this.searchResults = [];
            this.searchTotalCount = 0;
            return;
        }

        this.cancelPendingSearch();
        this.searchLoading = true;
        this.searchAbortController = new AbortController();

        try {
            const url = new URL(this.searchConfig.searchUrl, window.location.origin);
            url.searchParams.set('rootId', this.searchConfig.rootId);
            url.searchParams.set('query', query);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                signal: this.searchAbortController.signal
            });

            if (!response.ok) {
                throw new Error(`Search failed with status ${response.status}`);
            }

            const data = await response.json();
            this.searchResults = data.results ?? [];
            this.searchTotalCount = data.totalCount ?? 0;
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(error);
                this.searchResults = [];
                this.searchTotalCount = 0;
            }
        } finally {
            if (!this.searchAbortController?.signal.aborted) {
                this.searchLoading = false;
            }
        }
    },

    cancelPendingSearch() {
        if (this.searchAbortController) {
            this.searchAbortController.abort();
            this.searchAbortController = null;
        }
    },

    clearSearch() {
        this.searchQuery = '';
        this.searchResults = [];
        this.searchTotalCount = 0;
        this.searchLoading = false;
        this.cancelPendingSearch();

        this.$nextTick(() => {
            this.$refs.searchInput?.focus();
        });
    },

    get hasSearchQuery() {
        return (this.searchQuery ?? '').trim().length >= 2;
    },

    get hasResults() {
        return this.searchResults.length > 0;
    },

    get resultCountLabel() {
        const count = this.searchTotalCount;
        if (count === 1) {
            return (this.searchConfig.texts.resultCountSingle ?? '{0} result').replace('{0}', count);
        }

        return (this.searchConfig.texts.resultCountPlural ?? '{0} results').replace('{0}', count);
    }
    });
};
